'use client';

import { cn } from '@coas/ui/lib';
import { useTranslations } from 'next-intl';
import { AnimatePresence, motion } from 'framer-motion';
import IconCart from '~/shared/assets/icons/cart.svg';
import { useCartCount } from '../model/query';

interface CartIconBadgeProps {
    className?: string;
    iconClassName?: string;
    showZero?: boolean;
    ringClassName: string;
}

export const CartIconBadge = ({ className, iconClassName, showZero = false, ringClassName }: CartIconBadgeProps) => {
    const t = useTranslations('header');
    const { count, display } = useCartCount();
    const shouldShow = showZero ? true : count > 0;

    return (
        <span className={cn('relative inline-flex', className)}>
            <IconCart className={iconClassName} aria-hidden />

            <AnimatePresence initial={false}>
                {shouldShow && (
                    <motion.span
                        key={display}
                        initial={{ scale: 0.6, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 0.6, opacity: 0 }}
                        transition={{ type: 'spring', stiffness: 500, damping: 26 }}
                        className={cn(
                            'absolute -top-1.5 -right-1.5 md:-top-2 md:-right-2 z-10',
                            'min-w-[16px] h-[16px] md:min-w-[18px] md:h-[18px]',
                            'px-1 rounded-full',
                            'bg-red-600 text-neutral-50 text-[9px] md:text-10 font-700',
                            'leading-[16px] md:leading-[18px] text-center',
                            'ring-2',
                            'pointer-events-none select-none',
                            ringClassName,
                        )}
                        aria-label={`${t('cart')}: ${display}`}
                        aria-live='polite'
                    >
                        {display}
                    </motion.span>
                )}
            </AnimatePresence>

            <span className='sr-only'>{t('cart')}</span>
        </span>
    );
};
